import { useState, useEffect, ReactNode } from 'react'
import { useAuthStore } from '../../stores'
import {
  LayoutDashboard, Users, BarChart3, CreditCard,
  LogOut, Menu, X, Bell, Search, ChevronRight,
  FileText, UserCheck, Shield, Megaphone, Calendar
} from 'lucide-react'

export type AdminPage = 'overview' | 'users' | 'clubs' | 'claims' | 'insights' | 'blog' | 'fixtures' | 'affiliates' | 'announcements' | 'notifications' | 'reports' | 'subscriptions' | 'settings'

interface AdminLayoutProps {
  children: ReactNode
  activePage: AdminPage
  onNavigate: (page: AdminPage) => void
  onLogout: () => void
}

const NAV_ITEMS: { id: AdminPage; label: string; icon: any }[] = [
  { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'users', label: 'User Management', icon: Users },
  { id: 'clubs', label: 'Clubs', icon: Shield },
  { id: 'claims', label: 'Claims', icon: UserCheck },
  { id: 'insights', label: 'Player Insights', icon: BarChart3 },
  { id: 'blog', label: 'Blog', icon: FileText },
  { id: 'fixtures', label: 'Fixtures & Results', icon: Calendar },
  { id: 'affiliates', label: 'Affiliates', icon: CreditCard },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'reports', label: 'Reports', icon: FileText },
]

// Sub-admins only get content-focused pages (no user/club/claims management)
export const SUB_ADMIN_ALLOWED_PAGES: AdminPage[] = ['overview', 'blog', 'fixtures', 'announcements', 'notifications', 'settings']

export default function AdminLayout({ children, activePage, onNavigate, onLogout }: AdminLayoutProps) {
  const { user } = useAuthStore()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const isSubAdmin = user?.role === 'sub_admin'
  const visibleNavItems = isSubAdmin
    ? NAV_ITEMS.filter(item => SUB_ADMIN_ALLOWED_PAGES.includes(item.id))
    : NAV_ITEMS

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [sidebarOpen])

  const sidebar = (
    <div className="flex flex-col h-full bg-[#0d1117] border-r border-white/5">
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#7cff01]/10 flex items-center justify-center overflow-hidden">
            <img src="/scoutang-logo.svg" alt="" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-lg font-black text-white tracking-tight leading-none">Scouta</h1>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#7cff01]/60">Admin</p>
          </div>
        </div>
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-600 mt-3">System Controller</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleNavItems.map(item => {
          const isActive = activePage === item.id
          return (
            <button
              key={item.id}
              onClick={() => { onNavigate(item.id); setSidebarOpen(false) }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-200 ${
                isActive
                  ? 'bg-[#7cff01] text-black shadow-lg shadow-[#7cff01]/20'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
              {isActive && <ChevronRight size={14} className="ml-auto" />}
            </button>
          )
        })}
      </nav>

      {/* Bottom */}
      <div className="p-3 border-t border-white/5 space-y-1">
        {!isSubAdmin && (
          <button
            onClick={() => { onNavigate('reports'); setSidebarOpen(false) }}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold bg-[#7cff01]/10 text-[#7cff01] hover:bg-[#7cff01]/20 transition"
          >
            <FileText size={18} />
            <span>Reports</span>
          </button>
        )}
        <button
          onClick={() => { onLogout(); setSidebarOpen(false) }}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-slate-400 hover:text-red-400 hover:bg-red-500/5 transition"
        >
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#0b0f1a] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0 fixed inset-y-0 left-0 z-30">
        {sidebar}
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Mobile sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {sidebar}
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:ml-64 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-[#0b0f1a]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center gap-4 px-4 lg:px-8 h-16">
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition"
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>

            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search users, clubs..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#7cff01]/20 focus:border-[#7cff01]/30 transition"
                />
              </div>
            </div>

            {/* Right side */}
            <div className="flex items-center gap-3">
              <button className="relative p-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition">
                <Bell size={18} />
                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#7cff01] rounded-full" />
              </button>
              <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-white/10">
                <div className="w-8 h-8 rounded-lg bg-[#7cff01]/10 flex items-center justify-center text-[#7cff01] text-xs font-black">
                  {user?.name?.[0] || 'A'}
                </div>
                <span className="text-sm font-bold text-white">{user?.name || 'Admin'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}
